import React, { useState, useRef, useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './index.css';
import Header from './components/Header';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfUse from './components/TermsOfUse';
import DeleteAccount from './components/DeleteAccount';
import Section from './components/Section';
import { SectionState, RowState, SlotState, BarState, ChordCakeState, TimeSignature } from './types';
import { ChordObjectModelV3, KeySignature, transposeChord, getKeyTranspositionInterval, getSectionNote, EMPTY_SONG_META, readSongMeta, songMetaFields, countRowTimeSignatures, clearRowTimeSignatures, getRowSlots, getRowSlotCount, insertSlotInRow, mapSlots, rowFromSlots, updateSlotInRow, DEFAULT_TIME_SIGNATURE, canInsertBeside, getRowMeters, removeSlotFromBar, replaceBarInRow, songNeedsTimeSignature, applyChartDraft, describeChartChange, draftFromSections, mergeRowIntoPrevious, type ChartDraft, type ChartTextImportResult, type SongMeta } from '@chordbaker/music-theory';
import { CANT_ADD_INSIDE_TITLE, cantAddInsideMessage, clearBeatsInRows, meterChangeBeatsDialog, rowsLosingBeats } from './utils/beatsUtils';
import { generateUniqueId } from './utils/idUtils';
import { deepCopyRow, deepCopySection } from './utils/dataUtils';
import { isChordSheetEmpty } from './utils/sheetUtils';
import { importChordSheetFromFile, importChordSheetFromUrl } from './utils/importUtils';
import { exportChordSheet } from './utils/exportUtils';
import { isValidChordCakeState } from './utils/validation';
import { formatKeySignature } from './utils/noteHelpers';
import { CONSTANTS } from './constants';
import { track, EVENTS } from './analytics';
import { ImportHandler } from './components/ImportHandler';
import AppInstallBanner from './components/AppInstallBanner';
import { ThemeProvider, useTheme, Theme } from './contexts';
import ConfirmationModal from './components/ConfirmationModal';
import PasteChartModal from './components/PasteChartModal';
import KeySelectorModal from './components/KeySelectorModal';
import SongMetaChips from './components/SongMetaChips';
import TempoEditorModal from './components/TempoEditorModal';
import ChartReorderModal from './components/ChartReorderModal';
import TimeSignatureEditorModal from './components/TimeSignatureEditorModal';
import KeyChangeMethodModal from './components/KeyChangeMethodModal';
import LoadingModal from './components/LoadingModal';
import Footer from './components/Footer';

/**
 * Web React version of ChordBaker with identical functionality to React Native version.
 * 
 * KEY DIFFERENCE: React 18+ automatic batching requires setTimeout(0) workaround in handleSlotPress
 * to ensure state updates propagate before component re-renders. React Native doesn't have this
 * batching behavior, so the RN version works without setTimeout.
 * 
 * Otherwise, this maintains 100% architectural parity with react-native/ChordBaker/App.tsx:
 * - Same centralized active state management (activeSectionId, activeRowId, activeSlotId)
 * - Same 10-second auto-hide timer behavior
 * - Same double-tap slot interaction pattern (first tap shows controls, second opens editor)
 * - Same hierarchical control activation (slot activates parent row and section)
 */

// --- Types for Notification States ---
interface ConfirmationModalState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void; // Added onCancel to be explicit
  confirmText?: string;
  cancelText?: string;
  onAltConfirm?: () => void; // Optional alternative confirm action
  altConfirmText?: string; // Text for the alternative confirm button
  showCancelButton?: boolean; // false for a one-button notice
}

// --- Transpose tip helpers (shown periodically after Buffet import) ---
const incrementBuffetImportCount = (): void => {
  const count = parseInt(localStorage.getItem(CONSTANTS.BUFFET_IMPORT_COUNT_KEY) || '0', 10);
  localStorage.setItem(CONSTANTS.BUFFET_IMPORT_COUNT_KEY, String(count + 1));
};

const shouldShowTransposeTip = (): boolean => {
  if (localStorage.getItem(CONSTANTS.TRANSPOSE_TIP_DISMISSED_KEY) === 'true') return false;
  const count = parseInt(localStorage.getItem(CONSTANTS.BUFFET_IMPORT_COUNT_KEY) || '0', 10);
  return count > 0 && count % CONSTANTS.TRANSPOSE_TIP_FREQUENCY === 0;
};

const dismissTransposeTip = (): void => {
  localStorage.setItem(CONSTANTS.TRANSPOSE_TIP_DISMISSED_KEY, 'true');
};

// --- Helper functions to create default structures ---
const createDefaultSlot = (
  id: string,
  chord: ChordObjectModelV3 | null = null
): SlotState => ({
  id,
  chord,
  customText: null,
  lyrics: null,
});

// A row holds bars and a bar holds slots; today every bar holds one slot, so
// adding or removing a slot adds or removes a bar (the package's bar helpers
// keep that rule in one place).
const createDefaultRow = (): RowState =>
  rowFromSlots(
    generateUniqueId('row'),
    Array.from({ length: CONSTANTS.DEFAULT_SLOTS_COUNT }, () => createDefaultSlot(generateUniqueId('slot'))),
  );

const createDefaultSection = (title: string = '', rowsCount: number = 1): SectionState => ({
  id: generateUniqueId(),
  title,
  rows: Array.from({ length: rowsCount }, () => createDefaultRow()),
});

// RN-ALIGNED: Clean createStyles function matching React Native pattern
const createStyles = (theme: Theme) => ({
  appContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: theme.colors.background,
  },
  errorContainer: {
    position: 'fixed' as const,
    top: 96, // 24 * 4 = 96px (top-24)
    left: '50%',
    transform: 'translateX(-50%)',
    maxWidth: 600,
    width: `calc(100% - ${theme.spacing.lg * 2}px)`,
    zIndex: 50,
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.lg,
    borderRadius: theme.radii.lg,
    borderWidth: 1,
    borderStyle: 'solid' as const,
  },
  errorText: {
    flex: 1,
    fontSize: theme.typography.sizes.sm,
    fontWeight: theme.typography.weights.medium,
  },
  dismissButton: {
    marginLeft: theme.spacing.md,
    fontSize: theme.typography.sizes.sm,
    fontWeight: theme.typography.weights.semiBold,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    // WEB-SPECIFIC: Hover effect
    transition: `opacity ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    paddingTop: 96, // 24 * 4 = 96px (pt-24)
    paddingLeft: theme.spacing.sm, // 8px for mobile - matching RN version
    paddingRight: theme.spacing.sm, // 8px for mobile - matching RN version
    flex: 1,
  },
  titleContainer: {
    width: '100%',
    maxWidth: 896, // 4xl equivalent - 56rem = 896px
    marginTop: theme.spacing.lg,
    marginBottom: theme.spacing.lg,
    paddingLeft: theme.spacing.sm, // 8px left padding - matching RN version
    paddingRight: theme.spacing.sm, // 8px right padding - matching RN version
  },
  // Song key / time / tempo chips, aligned with the title text
  songMetaContainer: {
    width: '100%',
    maxWidth: 896,
    marginTop: -theme.spacing.sm, // tuck under the title: titleContainer's bottom margin is for the sections
    marginBottom: theme.spacing.lg,
    paddingLeft: theme.spacing.sm, // titleContainer's padding...
    paddingRight: theme.spacing.sm,
  },
  songMetaInner: {
    paddingLeft: theme.spacing.lg, // ...plus titleInput's
    paddingRight: theme.spacing.lg,
  },
  titleInput: {
    width: '100%',
    fontSize: theme.typography.sizes.display,
    fontWeight: theme.typography.weights.bold,
    backgroundColor: 'transparent',
    color: theme.colors.text,
    border: 'none',
    outline: 'none',
    resize: 'none' as const,
    overflow: 'hidden',
    lineHeight: theme.typography.lineHeights.tight,
    wordBreak: 'break-word' as const,
    paddingLeft: theme.spacing.lg, // 16px left padding - matching RN version
    paddingRight: theme.spacing.lg, // 16px right padding - matching RN version
  },
  sectionsContainer: {
    width: '100%',
    maxWidth: 896, // 4xl equivalent
  },
});

const ThemedApp: React.FC = () => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);


  const [sections, setSections] = useState<SectionState[]>([createDefaultSection()]);
  const [projectTitle, setProjectTitle] = useState<string>('');
  const [showNotes, setShowNotes] = useState<boolean>(true);
  const [showLyrics, setShowLyrics] = useState<boolean>(true);
  const [showDegrees, setShowDegrees] = useState<boolean>(false);
  // Beat dots + the beats button (in memory like the other switches; RN remembers it per song)
  const [showBeats, setShowBeats] = useState<boolean>(false);
  const [selectedKey, setSelectedKey] = useState<KeySignature | null>(null);
  // Song tempo + global time signature (mutually exclusive with row time signatures)
  const [songMeta, setSongMeta] = useState<SongMeta>(EMPTY_SONG_META);
  const [songMetaEditor, setSongMetaEditor] = useState<'time' | 'tempo' | null>(null);
  // Each row's meter, and whether the song has one at all (no meter → no beats)
  const rowMeters = useMemo(() => getRowMeters(sections, songMeta.timeSignature), [sections, songMeta.timeSignature]);
  const beatsNeedTimeSignature = useMemo(
    () => songNeedsTimeSignature(sections, songMeta.timeSignature),
    [sections, songMeta.timeSignature],
  );
  const [guitarShapes, setGuitarShapes] = useState<Record<string, string>>({});
  const [isKeySelectorModalOpen, setIsKeySelectorModalOpen] = useState<boolean>(false);
  // The reorder sheet, opened from the header's Reorder chart
  const [isReorderOpen, setIsReorderOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('Processing...');
  const [fileError, setFileError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [isPasteChartOpen, setIsPasteChartOpen] = useState<boolean>(false);
  const [isPasteReminderOpen, setIsPasteReminderOpen] = useState<boolean>(false);
  const [confirmationModalInfo, setConfirmationModalInfo] = useState<ConfirmationModalState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    onCancel: () => { },
    onAltConfirm: undefined,
    altConfirmText: undefined,
  });

  // Ref for the title textarea to adjust height on content changes
  const titleTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Key change method modal states
  const [isKeyChangeMethodModalOpen, setIsKeyChangeMethodModalOpen] = useState<boolean>(false);
  const [pendingKeyChange, setPendingKeyChange] = useState<{ fromKey: KeySignature, toKey: KeySignature, selection: KeySignature } | null>(null);

  // State for managing active elements (centralized state management)
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const [activeSlotId, setActiveSlotId] = useState<string | null>(null);
  // A just-added slot whose chord editor opens right away (saves the second click)
  const [slotToOpenChordEditor, setSlotToOpenChordEditor] = useState<string | null>(null);

  // Auto-hide timer for controls (like React Native version)
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to find which row and section a slot belongs to (from RN version)
  const findSlotContext = (targetSlotId: string): { sectionId?: string, rowId?: string } => {
    for (const section of sections) {
      for (const row of section.rows) {
        if (getRowSlots(row).some(slot => slot.id === targetSlotId)) {
          return { sectionId: section.id, rowId: row.id };
        }
      }
    }
    return {};
  };

  // Centralized control visibility function (updated to match RN version's superior implementation)
  const getControlVisibility = (elementId: string, elementType: 'section' | 'row' | 'slot'): boolean => {
    const slotContext = activeSlotId ? findSlotContext(activeSlotId) : {};

    switch (elementType) {
      case 'section':
        // Section controls visible if: section is active OR a child row is active OR a child slot is active
        return Boolean(
          activeSectionId === elementId ||
          (activeRowId && sections.find(s => s.id === elementId)?.rows.some(r => r.id === activeRowId)) ||
          (activeSlotId && slotContext.sectionId === elementId)
        );

      case 'row':
        // Row controls visible if: row is active OR a child slot is active
        return Boolean(
          activeRowId === elementId ||
          (activeSlotId && slotContext.rowId === elementId)
        );

      case 'slot':
        // Slot controls visible if: slot is active
        return Boolean(activeSlotId === elementId);

      default:
        return false;
    }
  };

  // Clear all active states (hide all controls)
  const clearAllActiveStates = () => {
    setActiveSectionId(null);
    setActiveRowId(null);
    setActiveSlotId(null);
  };

  // Reset the auto-hide timer
  const resetAutoHideTimer = () => {
    // Clear existing timer
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    // Set new timer only if there are active controls
    if (activeSectionId || activeRowId || activeSlotId) {
      hideTimerRef.current = setTimeout(() => {
        clearAllActiveStates();
      }, CONSTANTS.AUTO_HIDE_DELAY);
    }
  };

  // Effect to manage auto-hide timer
  useEffect(() => {
    resetAutoHideTimer();
    // Cleanup timer on unmount
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [activeSectionId, activeRowId, activeSlotId]); // Dependency on all active states

  // Effect to adjust title textarea height when projectTitle changes
  useEffect(() => {
    if (titleTextareaRef.current) {
      titleTextareaRef.current.style.height = 'auto';
      titleTextareaRef.current.style.height = `${titleTextareaRef.current.scrollHeight}px`;
    }
  }, [projectTitle]);

  // Global click handler to clear active states when clicking outside
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as Element;

      // Check if click is outside chord sheets area (but not on controls)
      if (!target.closest('.chord-sheet-area') &&
        !target.closest('.section-controls') &&
        !target.closest('.row-action-button') &&
        !target.closest('.slot-controls')) {
        clearAllActiveStates();
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, []);

  // Active state management handlers
  const handleSectionPress = (sectionId: string) => {
    resetAutoHideTimer(); // Reset timer on user interaction
    setActiveSectionId(prev => prev === sectionId ? null : sectionId); // Toggle behavior
    // When section is activated, clear row and slot states
    setActiveRowId(null);
    setActiveSlotId(null);
  };

  // Like handleSectionPress, but never hides an active section (title focus/click)
  const activateSection = (sectionId: string) => {
    resetAutoHideTimer();
    setActiveSectionId(sectionId);
    setActiveRowId(null);
    setActiveSlotId(null);
  };

  const handleRowPress = (rowId: string) => {
    resetAutoHideTimer(); // Reset timer on user interaction

    // Find the section that contains this row and activate it
    const section = sections.find(s => s.rows.some(r => r.id === rowId));
    if (section) {
      setActiveSectionId(section.id);
    }

    setActiveRowId(prev => prev === rowId ? null : rowId); // Toggle behavior
    setActiveSlotId(null);
  };

  const handleSlotPress = (slotId: string) => {
    resetAutoHideTimer(); // Reset timer on user interaction

    // Find the section and row that contain this slot and activate them
    let foundSection: SectionState | undefined;
    let foundRow: RowState | undefined;

    for (const section of sections) {
      for (const row of section.rows) {
        if (getRowSlots(row).some(slot => slot.id === slotId)) {
          foundSection = section;
          foundRow = row;
          break;
        }
      }
      if (foundSection) break;
    }

    // Only activate parents if the slot is being activated (not deactivated)
    if (activeSlotId !== slotId && foundSection && foundRow) {
      setActiveSectionId(foundSection.id);
      setActiveRowId(foundRow.id);
    }

    // WEB-SPECIFIC FIX: React 18+ automatic batching requires setTimeout(0) to ensure
    // state updates are properly propagated before the Slot component re-renders.
    // React Native doesn't have this batching behavior, so it works without setTimeout.
    setTimeout(() => {
      setActiveSlotId(prev => prev === slotId ? null : slotId);
    }, 0);
  };

  const clearActiveSection = () => {
    // Don't reset timer for clear actions - these are typically automatic
    setActiveSectionId(null);
  };
  const clearActiveRow = () => {
    // Don't reset timer for clear actions - these are typically automatic
    setActiveRowId(null);
  };
  const clearActiveSlot = () => {
    // Don't reset timer for clear actions - these are typically automatic
    setActiveSlotId(null);
  };

  // The reorder sheet's Apply: its draft (order, copies, deletions) applied to
  // the current sections, so an edit made while it was open is kept; every row
  // keeps its meter (applyChartDraft)
  const chartDraftOptions = {
    newSectionId: () => generateUniqueId('section'),
    copyRow: deepCopyRow,
    globalTimeSignature: songMeta.timeSignature,
    generateId: (kind: string) => generateUniqueId(kind),
  };

  const handleApplyChartDraft = (draft: ChartDraft) => {
    setIsReorderOpen(false);
    const next = applyChartDraft(sections, draft, chartDraftOptions);
    if (!describeChartChange(sections, next)) return;
    resetAutoHideTimer();
    setSections(next);
  };

  // "Keep Chords" in the row delete dialog: the row's bars join the row above
  // it (or the last row of the section above), through the same draft
  // operation the Reorder chart sheet uses
  const handleMergeRowIntoPrevious = (rowId: string) => {
    resetAutoHideTimer();
    setSections(prevSections =>
      applyChartDraft(prevSections, mergeRowIntoPrevious(draftFromSections(prevSections), rowId).draft, chartDraftOptions),
    );
  };

  const handleUpdateSectionTitle = (sectionId: string, newTitle: string) => {
    resetAutoHideTimer(); // Reset timer on user interaction
    setSections(prevSections =>
      prevSections.map(section =>
        section.id === sectionId ? { ...section, title: newTitle } : section
      )
    );
  };

  const handleConfirmDeleteRowInSection = (rowIdToDelete: string, sectionId: string) => {
    resetAutoHideTimer(); // Reset timer on user action
    setSections(prevSections =>
      prevSections.map(section => {
        if (section.id === sectionId) {
          if (section.rows.length <= CONSTANTS.MIN_ROWS_PER_SECTION) {
            console.warn(`Attempted to delete the last row in section ${sectionId}. Operation aborted.`);
            return section;
          }
          const updatedRows = section.rows.filter(row => row.id !== rowIdToDelete);
          return { ...section, rows: updatedRows };
        }
        return section;
      })
    );
  };

  const handleDeleteRowInSection = (rowIdToDelete: string, sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return; // Section not found

    const row = section.rows.find(r => r.id === rowIdToDelete);
    if (!row) return; // Row not found

    if (section.rows.length <= CONSTANTS.MIN_ROWS_PER_SECTION) {
      return; // Can't delete the last row
    }

    // Check if row has any chord content
    const hasContent = getRowSlots(row).some(slot => slot.chord !== null);
    const hasPreviousRow = section.rows[0].id !== rowIdToDelete || sections[0].id !== sectionId;

    if (hasContent && hasPreviousRow) {
      setConfirmationModalInfo({
        isOpen: true,
        title: 'Delete Row',
        message: 'Do you want to delete this row and all its chords, or keep the chords by moving them to the end of the previous row?',
        onConfirm: () => handleMergeRowIntoPrevious(rowIdToDelete),
        confirmText: 'Keep Chords',
        onAltConfirm: () => handleConfirmDeleteRowInSection(rowIdToDelete, sectionId),
        altConfirmText: 'Delete Row',
        onCancel: () => {
          setConfirmationModalInfo(prev => ({ ...prev, isOpen: false, onAltConfirm: undefined, altConfirmText: undefined }));
        },
      });
    } else if (hasContent) {
      // Show confirmation modal if row has chords
      setConfirmationModalInfo({
        isOpen: true,
        title: 'Delete Row',
        message: 'Are you sure you want to delete this row and all its chords? This action cannot be undone.',
        onConfirm: () => handleConfirmDeleteRowInSection(rowIdToDelete, sectionId),
        confirmText: 'Delete',
        onCancel: () => {
          setConfirmationModalInfo(prev => ({ ...prev, isOpen: false }));
        },
      });
    } else {
      // No chords, delete directly
      handleConfirmDeleteRowInSection(rowIdToDelete, sectionId);
    }
  };

  const handleAddRowInSection = (afterRowId: string, sectionId: string) => {
    resetAutoHideTimer(); // Reset timer on user action
    const newRow = createDefaultRow();
    setSections(prevSections =>
      prevSections.map(section => {
        if (section.id === sectionId) {
          const rowIndex = section.rows.findIndex(row => row.id === afterRowId);
          const newRows = [...section.rows];
          newRows.splice(rowIndex + 1, 0, newRow);
          return { ...section, rows: newRows };
        }
        return section;
      })
    );
  };

  const handleDuplicateRowInSection = (rowIdToDuplicate: string, sectionId: string) => {
    resetAutoHideTimer(); // Reset timer on user action
    setSections(prevSections =>
      prevSections.map(section => {
        if (section.id === sectionId) {
          const originalRowIndex = section.rows.findIndex(row => row.id === rowIdToDuplicate);
          if (originalRowIndex === -1) return section;

          const originalRow = section.rows[originalRowIndex];
          const duplicatedRow = deepCopyRow(originalRow);

          const newRows = [...section.rows];
          newRows.splice(originalRowIndex + 1, 0, duplicatedRow);
          return { ...section, rows: newRows };
        }
        return section;
      })
    );
  };

  const closeConfirmation = () => setConfirmationModalInfo(prev => ({ ...prev, isOpen: false }));

  // A row's time signature. When the change moves split bars to another meter
  // (this row's, or the rows after it that carry it), ask first, then turn
  // their slots back into whole bars.
  const handleSetRowTimeSignature = (
    rowId: string,
    sectionId: string,
    timeSignature: TimeSignature | null,
  ) => {
    resetAutoHideTimer();
    const withSignature = (prev: SectionState[]) => prev.map(section =>
      section.id !== sectionId ? section : {
        ...section,
        rows: section.rows.map(row => (row.id === rowId ? { ...row, timeSignature } : row)),
      });
    const nextSections = withSignature(sections);
    const losing = rowsLosingBeats(
      { sections, timeSignature: songMeta.timeSignature },
      { sections: nextSections, timeSignature: songMeta.timeSignature },
    );
    const commit = () => setSections(prev => clearBeatsInRows(withSignature(prev), losing));
    if (losing.size === 0) {
      commit();
      return;
    }
    const { title, message } = meterChangeBeatsDialog(
      // Clearing a row's signature puts it in the one it inherits
      meterOfRow(nextSections, rowId),
      losing.size === 1 && losing.has(rowId) ? 'row' : 'following',
    );
    setConfirmationModalInfo({
      isOpen: true,
      title,
      message,
      confirmText: 'Change',
      onConfirm: commit,
      onCancel: closeConfirmation,
    });
  };

  // Section repeat count ("×2") and note; null clears
  const handleSetSectionRepeat = (sectionId: string, repeat: number | null) => {
    resetAutoHideTimer();
    setSections(prevSections =>
      prevSections.map(section =>
        section.id === sectionId ? { ...section, repeat } : section,
      ),
    );
  };

  const handleSetSectionNote = (sectionId: string, note: string | null) => {
    resetAutoHideTimer();
    setSections(prevSections =>
      prevSections.map(section =>
        section.id === sectionId ? { ...section, note } : section,
      ),
    );
  };

  // Applies `update` to one row. It also gets the sections it runs on, for
  // the row's meter.
  const updateRow = (
    sectionId: string,
    rowId: string,
    update: (row: RowState, prevSections: SectionState[]) => RowState,
  ) => {
    resetAutoHideTimer(); // Reset timer on user action
    setSections(prevSections =>
      prevSections.map(section =>
        section.id !== sectionId ? section : {
          ...section,
          rows: section.rows.map(row => (row.id === rowId ? update(row, prevSections) : row)),
        },
      ),
    );
  };

  const meterOfRow = (prevSections: SectionState[], rowId: string) =>
    getRowMeters(prevSections, songMeta.timeSignature).get(rowId) ?? DEFAULT_TIME_SIGNATURE;

  // + before/after adds a whole bar, only at a bar's edges (inner slots of a
  // split bar explain why instead: handleCantAddInside)
  // A new slot becomes the active one (so the next add follows it) and opens
  // its chord editor; closing it without applying leaves a rest.
  const openNewSlot = (newSlotId: string) => {
    setActiveSlotId(newSlotId);
    setSlotToOpenChordEditor(newSlotId);
  };

  const handleAddSlot = (afterSlotId: string, rowId: string, sectionId: string) => {
    const newSlotId = generateUniqueId('slot');
    updateRow(sectionId, rowId, row =>
      canInsertBeside(row, afterSlotId).after
        ? insertSlotInRow(row, createDefaultSlot(newSlotId, null), { after: afterSlotId })
        : row,
    );
    openNewSlot(newSlotId);
  };

  const handleAddSlotBefore = (beforeSlotId: string, rowId: string, sectionId: string) => {
    const newSlotId = generateUniqueId('slot');
    updateRow(sectionId, rowId, row =>
      canInsertBeside(row, beforeSlotId).before
        ? insertSlotInRow(row, createDefaultSlot(newSlotId, null), { before: beforeSlotId })
        : row,
    );
    openNewSlot(newSlotId);
  };

  const handleCantAddInside = (slotId: string, rowId: string, sectionId: string) => {
    resetAutoHideTimer();
    const row = sections.find(section => section.id === sectionId)?.rows.find(r => r.id === rowId);
    const bar = row?.bars.find(b => b.slots.some(slot => slot.id === slotId));
    if (!bar) return;
    setConfirmationModalInfo({
      isOpen: true,
      title: CANT_ADD_INSIDE_TITLE,
      message: cantAddInsideMessage(bar),
      confirmText: 'OK',
      showCancelButton: false,
      onConfirm: () => { },
      onCancel: closeConfirmation,
    });
  };

  // Inside a split bar the slot's beats go to its neighbour; a slot alone in
  // its bar takes the bar with it
  const handleDeleteSlot = (slotIdToDelete: string, rowId: string, sectionId: string) => {
    updateRow(sectionId, rowId, (row, prevSections) =>
      getRowSlotCount(row) <= CONSTANTS.MIN_SLOTS_PER_ROW
        ? row
        : removeSlotFromBar(row, slotIdToDelete, meterOfRow(prevSections, rowId)),
    );
  };

  // The bar editor's Apply: the edited bar replaces the original (the editor
  // ran the shared bar operations on its draft), and the slot it left
  // selected becomes the active one — by id, since a slot the editor added
  // isn't in `sections` until the update lands
  const handleApplyBar = (rowId: string, barId: string, bar: BarState, focusSlotId: string, sectionId: string) => {
    updateRow(sectionId, rowId, row => replaceBarInRow(row, barId, bar));
    setActiveSectionId(sectionId);
    setActiveRowId(rowId);
    setActiveSlotId(focusSlotId);
  };

  const handleUpdateChord = (slotId: string, newChordData: ChordObjectModelV3 | null, rowId: string, sectionId: string, customText?: string) => {
    updateRow(sectionId, rowId, row =>
      updateSlotInRow(row, slotId, slot => ({ ...slot, chord: newChordData, customText: customText ?? null })),
    );
  };

  const handleUpdateLyrics = (slotId: string, lyrics: string, rowId: string, sectionId: string) => {
    updateRow(sectionId, rowId, row => updateSlotInRow(row, slotId, slot => ({ ...slot, lyrics })));
  };

  const handleAddSectionAfterRow = (clickedRowId: string, sectionId: string) => {
    resetAutoHideTimer(); // Reset timer on user action
    setSections(prevSections => {
      const currentSectionIndex = prevSections.findIndex(s => s.id === sectionId);
      if (currentSectionIndex === -1) return prevSections;

      const currentSection = prevSections[currentSectionIndex];
      const clickedRowIndex = currentSection.rows.findIndex(r => r.id === clickedRowId);
      if (clickedRowIndex === -1) return prevSections;

      const newSectionsArray = [...prevSections];
      const newSection: SectionState = {
        id: generateUniqueId('section'),
        title: '',
        rows: [],
      };

      if (clickedRowIndex === currentSection.rows.length - 1) {
        newSection.rows = [createDefaultRow()];
      } else {
        const rowsToMove = currentSection.rows.slice(clickedRowIndex + 1);
        newSection.rows = rowsToMove;
        const updatedCurrentSectionRows = currentSection.rows.slice(0, clickedRowIndex + 1);
        const updatedCurrentSection = { ...currentSection, rows: updatedCurrentSectionRows };
        newSectionsArray.splice(currentSectionIndex, 1, updatedCurrentSection);
      }
      newSectionsArray.splice(currentSectionIndex + 1, 0, newSection);
      return newSectionsArray;
    });
  };

  const handleAddSectionAfterSection = (sectionId: string) => {
    resetAutoHideTimer(); // Reset timer on user action
    setSections(prevSections => {
      const sectionIndex = prevSections.findIndex(s => s.id === sectionId);
      if (sectionIndex === -1) return prevSections;

      const newSection: SectionState = {
        id: generateUniqueId('section'),
        title: '',
        rows: [createDefaultRow()],
      };
      const newSectionsArray = [...prevSections];
      newSectionsArray.splice(sectionIndex + 1, 0, newSection);
      return newSectionsArray;
    });
  };

  const handleConfirmDeleteSection = (idToDelete: string) => {
    resetAutoHideTimer(); // Reset timer on user action
    setSections(prevSections => {
      if (prevSections.length <= CONSTANTS.MIN_SECTIONS && prevSections.find(s => s.id === idToDelete)) {
        console.warn("Attempted to delete the last section. Operation aborted.");
        return prevSections;
      }
      return prevSections.filter(section => section.id !== idToDelete);
    });
  };

  const handleMoveRowsToAboveSection = (idToMove: string) => {
    resetAutoHideTimer(); // Reset timer on user action
    setSections(prevSections => {
      const sectionIndex = prevSections.findIndex(s => s.id === idToMove);
      if (sectionIndex === -1 || sectionIndex === 0) {
        console.warn("Cannot move rows: section is the first or not found.");
        return prevSections;
      }

      const sectionToModify = prevSections[sectionIndex];
      const sectionAbove = prevSections[sectionIndex - 1];

      const updatedSectionAbove = {
        ...sectionAbove,
        rows: [...sectionAbove.rows, ...sectionToModify.rows],
      };

      const newSections = prevSections.filter(s => s.id !== idToMove);
      newSections[sectionIndex - 1] = updatedSectionAbove;
      return newSections;
    });
  };

  const handleDeleteSection = (sectionId: string) => {
    const sectionIndex = sections.findIndex(s => s.id === sectionId);
    if (sectionIndex === -1) return; // Section not found
    const section = sections[sectionIndex];

    if (sections.length <= CONSTANTS.MIN_SECTIONS) {
      return;
    }

    const hasContent = section.title.trim() !== '' || section.rows.length > 1 ||
      section.rows.some(row => getRowSlots(row).some(slot => slot.chord !== null)) ||
      getSectionNote(section) !== null;
    const isUnderAnotherSection = sectionIndex > 0;

    if (hasContent && isUnderAnotherSection) {
      setConfirmationModalInfo({
        isOpen: true,
        title: 'Delete Section',
        message: `Do you want to delete section "${section.title || 'Untitled Section'}" and all its content, or keep chords by moving them to the section above ("${sections[sectionIndex - 1].title || 'Untitled Section'}")?`,
        onAltConfirm: () => handleConfirmDeleteSection(sectionId),
        altConfirmText: 'Delete Everything',
        onConfirm: () => handleMoveRowsToAboveSection(sectionId),
        confirmText: 'Keep Chords',
        onCancel: () => {
          setConfirmationModalInfo(prev => ({ ...prev, isOpen: false, onAltConfirm: undefined, altConfirmText: undefined }));
        },
      });
    } else if (hasContent && !isUnderAnotherSection) {
      // First section with content: confirm deletion without option to move
      setConfirmationModalInfo({
        isOpen: true,
        title: 'Delete Section',
        message: `Are you sure you want to delete section "${section.title || 'Untitled Section'}" and all its content? This action cannot be undone.`,
        onConfirm: () => handleConfirmDeleteSection(sectionId),
        confirmText: 'Delete',
        onCancel: () => {
          setConfirmationModalInfo(prev => ({ ...prev, isOpen: false }));
        },
        // No onAltConfirm needed for this specific case
      });
    } else {
      // No content, or other unhandled cases (though last section is caught above)
      handleConfirmDeleteSection(sectionId);
    }
  };

  const handleDuplicateSection = (sectionIdToDuplicate: string) => {
    resetAutoHideTimer(); // Reset timer on user action
    setSections(prevSections => {
      const originalSectionIndex = prevSections.findIndex(s => s.id === sectionIdToDuplicate);
      if (originalSectionIndex === -1) {
        console.warn(`Section with ID ${sectionIdToDuplicate} not found for duplication.`);
        return prevSections;
      }
      const originalSection = prevSections[originalSectionIndex];
      // deepCopySection regenerates every section, row, bar and slot id
      const duplicatedSection = deepCopySection(originalSection);

      const newSectionsArray = [...prevSections];
      newSectionsArray.splice(originalSectionIndex + 1, 0, duplicatedSection);
      return newSectionsArray;
    });
  };

  // Song tempo / global time signature. A global time signature replaces the
  // per-row ones: when rows have their own, ask first, then clear them.
  const applyTempo = (tempo: number | null) => {
    setSongMetaEditor(null);
    setSongMeta(prev => ({ ...prev, tempo }));
  };

  // One question at most: it folds in clearing the rows' own signatures and
  // turning split bars whose meter changes back into whole bars.
  const applySongTimeSignature = (timeSignature: TimeSignature | null) => {
    setSongMetaEditor(null);
    const replacesRowSignatures = timeSignature !== null && countRowTimeSignatures(sections) > 0;
    const withoutRowSignatures = (prev: SectionState[]) => (replacesRowSignatures ? clearRowTimeSignatures(prev) : prev);
    const losing = rowsLosingBeats(
      { sections, timeSignature: songMeta.timeSignature },
      { sections: withoutRowSignatures(sections), timeSignature },
    );
    const commit = () => {
      setSongMeta(prev => ({ ...prev, timeSignature }));
      setSections(prev => clearBeatsInRows(withoutRowSignatures(prev), losing));
    };
    if (losing.size > 0) {
      const { title, message } = meterChangeBeatsDialog(timeSignature, 'song', replacesRowSignatures);
      setConfirmationModalInfo({
        isOpen: true,
        title,
        message,
        confirmText: 'Change',
        onConfirm: commit,
        onCancel: closeConfirmation,
      });
      return;
    }
    if (replacesRowSignatures) {
      setConfirmationModalInfo({
        isOpen: true,
        title: 'Replace row time signatures?',
        message: 'Setting a time signature for the whole song removes the ones set on individual rows.',
        confirmText: 'Replace',
        onConfirm: commit,
        onCancel: closeConfirmation,
      });
      return;
    }
    commit();
  };

  const handleSaveChordSheet = async () => {
    try {
      setIsLoading(true);
      setLoadingMessage('Exporting chord chart...');
      setFileError(null); // Clear any existing errors
      track(EVENTS.chart_exported, { sections_count: sections.length });
      await exportChordSheet({
        title: projectTitle,
        sections,
        key: selectedKey,
        songMeta,
        guitarShapes,
      });
    } catch (error) {
      console.error('Error exporting chord chart:', error);
      setFileError(`Error exporting chord chart: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle import from Buffet (Requirements: 1.3, 1.4, 1.5, 7.1, 7.2, 7.3, 7.5)
  const handleImportFromBuffet = async (chartId: string) => {
    try {
      setIsLoading(true);
      setLoadingMessage('Loading chart from Buffet...');
      setImportError(null);

      // Fetch chart data from Buffet (Requirement: 1.3)
      // importChordSheetFromUrl already extracts the 'chart' property from the API response
      const chartData = await importChordSheetFromUrl(chartId);

      // Validate chart data (Requirement: 1.4)
      // chartData is already the ChordCakeState object (not the full API response)
      if (!isValidChordCakeState(chartData)) {
        throw new Error('Invalid chart format. The chart may be corrupted.');
      }

      // Load into editor (Requirements: 1.4, 6.1, 6.2, 6.3, 6.4, 6.5)
      setProjectTitle(chartData.title || '');
      setSections(chartData.sections || [createDefaultSection()]);

      if (chartData.key) {
        setSelectedKey(chartData.key);
        setShowDegrees(true);
      }

      setSongMeta(readSongMeta(chartData));

      // Preserve guitar shapes from imported chart
      setGuitarShapes(chartData.guitarShapes ?? {});

      // Clear active states after successful import (Requirement: 1.5)
      clearAllActiveStates();
      track(EVENTS.chart_imported, {
        source: 'buffet_url',
        sections_count: (chartData.sections || []).length
      });

      // Show transpose tip every Nth Buffet import
      incrementBuffetImportCount();
      if (shouldShowTransposeTip()) {
        setConfirmationModalInfo({
          isOpen: true,
          title: 'Transpose Tip',
          message: "Don't like this key? You can easily transpose the song to whatever key you want!",
          confirmText: 'Change Key',
          cancelText: 'Got it',
          onConfirm: () => {
            setConfirmationModalInfo(prev => ({ ...prev, isOpen: false }));
            setIsKeySelectorModalOpen(true);
          },
          onCancel: () => {
            setConfirmationModalInfo(prev => ({ ...prev, isOpen: false }));
          },
          altConfirmText: "Don't Show Again",
          onAltConfirm: () => {
            dismissTransposeTip();
            setConfirmationModalInfo(prev => ({ ...prev, isOpen: false }));
          },
        });
      }
    } catch (error) {
      console.error('Import from Buffet error:', error);
      setImportError(
        error instanceof Error
          ? error.message
          : 'Failed to load chart from Buffet'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadChordSheet = (file: File) => {
    setIsLoading(true);
    setLoadingMessage('Loading chord chart...');
    setFileError(null); // Clear any existing errors

    importChordSheetFromFile(file, {
      onSuccess: (data) => {
        setProjectTitle(data.title);
        setSections(data.sections);

        // Restore selected key if it exists in the loaded data
        if (data.key !== undefined) {
          setSelectedKey(data.key);
          setShowDegrees(true); // Automatically enable degrees when key is loaded from file
        }

        setSongMeta(data.songMeta);

        // Preserve guitar shapes from imported file
        setGuitarShapes(data.guitarShapes ?? {});

        track(EVENTS.chart_imported, {
          source: 'file',
          sections_count: data.sections.length
        });
        setIsLoading(false);
      },
      onError: (error) => {
        setFileError(error);
        setIsLoading(false);
      },
      currentSongTitle: projectTitle,
      currentSections: sections,
      onConfirmReplace: () => {
        // Clear active states when replacing
        setActiveSectionId(null);
        setActiveRowId(null);
        setActiveSlotId(null);
      },
      onConfirmEmptyFile: (proceedCallback) => {
        setIsLoading(false); // Hide loading modal to show confirmation
        setConfirmationModalInfo({
          isOpen: true,
          title: 'Empty File Detected',
          message: 'The selected file appears to be empty (no title or chords). Would you like to load it anyway?',
          onConfirm: () => {
            setIsLoading(true); // Resume loading
            proceedCallback();
            setConfirmationModalInfo(prev => ({ ...prev, isOpen: false }));
          },
          onCancel: () => {
            setIsLoading(false);
            setConfirmationModalInfo(prev => ({ ...prev, isOpen: false }));
          },
        });
      },
      onWarnNoChords: (proceedCallback) => {
        setIsLoading(false); // Hide loading modal to show confirmation
        setConfirmationModalInfo({
          isOpen: true,
          title: 'No Chords Found',
          message: 'The chord chart appears to contain no chords or title. You may want to check if this is the correct file. Do you want to proceed anyway?',
          onConfirm: () => {
            setIsLoading(true); // Resume loading
            proceedCallback();
            setConfirmationModalInfo(prev => ({ ...prev, isOpen: false }));
          },
          onCancel: () => {
            setIsLoading(false);
            setConfirmationModalInfo(prev => ({ ...prev, isOpen: false }));
          },
        });
      },
      onConfirmReplaceExisting: (proceedCallback) => {
        setIsLoading(false); // Hide loading modal to show confirmation
        setConfirmationModalInfo({
          isOpen: true,
          title: 'Replace Current Chord Chart',
          message: 'Importing a new chord chart will replace your current progress. Are you sure you want to continue?',
          onConfirm: () => {
            setIsLoading(true); // Resume loading
            proceedCallback();
            setConfirmationModalInfo(prev => ({ ...prev, isOpen: false }));
          },
          onCancel: () => {
            setIsLoading(false);
            setConfirmationModalInfo(prev => ({ ...prev, isOpen: false }));
          },
        });
      },
    });
  };

  // Load a chart parsed from pasted text, then remind the user to check it
  const loadPastedChart = ({ song, format, keySource }: ChartTextImportResult) => {
    setProjectTitle(song.title);
    setSections(song.sections);
    setSelectedKey(song.key);
    if (song.key) {
      setShowDegrees(true);
    }
    setSongMeta(readSongMeta(song));
    setGuitarShapes({});
    clearAllActiveStates();
    track(EVENTS.chart_imported, {
      source: 'paste',
      format,
      key_source: keySource,
      sections_count: song.sections.length,
    });
    setIsPasteReminderOpen(true);
  };

  const handleImportPastedChart = (result: ChartTextImportResult) => {
    setIsPasteChartOpen(false);
    if (isChordSheetEmpty(projectTitle, sections)) {
      loadPastedChart(result);
      return;
    }
    setConfirmationModalInfo({
      isOpen: true,
      title: 'Replace Current Chord Chart',
      message: 'Importing a new chord chart will replace your current progress. Are you sure you want to continue?',
      onConfirm: () => loadPastedChart(result),
      onCancel: () => setConfirmationModalInfo(prev => ({ ...prev, isOpen: false })),
    });
  };

  return (
    <BrowserRouter>
      <ImportHandler onImport={handleImportFromBuffet} />
      <AppInstallBanner
        chartData={{
          version: 4,
          title: projectTitle,
          sections,
          key: selectedKey,
          ...songMetaFields(songMeta),
          guitarShapes,
        }}
      />
      <Routes>
        <Route
          path="/privacy"
          element={<PrivacyPolicy />}
        />
        <Route
          path="/terms-of-use"
          element={<TermsOfUse />}
        />
        <Route
          path="/delete-account"
          element={<DeleteAccount />}
        />
        <Route
          path="/*"
          element={
            <>
              {/* RN-ALIGNED: Use theme object instead of Tailwind classes */}
              <div style={styles.appContainer}>
                <Header
                  onSave={handleSaveChordSheet}
                  onLoad={handleLoadChordSheet}
                  onOpenPasteChart={() => setIsPasteChartOpen(true)}
                  onOpenReorderChart={() => setIsReorderOpen(true)}
                  showNotes={showNotes}
                  onToggleNotes={() => setShowNotes(!showNotes)}
                  showLyrics={showLyrics}
                  onToggleLyrics={() => setShowLyrics(!showLyrics)}
                  showBeats={showBeats}
                  beatsNeedTimeSignature={beatsNeedTimeSignature}
                  onToggleBeats={() => {
                    // No meter at all: beats can't be counted, so set one first
                    if (beatsNeedTimeSignature) {
                      setSongMetaEditor('time');
                    } else {
                      setShowBeats(!showBeats);
                    }
                  }}
                  showDegrees={showDegrees}
                  onToggleDegrees={() => {
                    if (!selectedKey && !showDegrees) {
                      setIsKeySelectorModalOpen(true);
                    } else {
                      setShowDegrees(!showDegrees);
                    }
                  }}
                  selectedKey={selectedKey}
                  onSetSelectedKey={(key) => {
                    setSelectedKey(key);
                    if (key) {
                      setShowDegrees(true);
                    }
                    setIsKeySelectorModalOpen(false);
                  }}
                  onOpenKeySelector={() => setIsKeySelectorModalOpen(true)}
                  isLoading={isLoading}
                />
                <KeySelectorModal
                  isOpen={isKeySelectorModalOpen}
                  onClose={() => setIsKeySelectorModalOpen(false)}
                  onSelectKey={(key) => {
                    // Check if we should show the key change method modal
                    const currentKeyText = selectedKey ? formatKeySignature(selectedKey) : null;
                    const newKeyText = key ? formatKeySignature(key) : null;

                    if (key && selectedKey && newKeyText !== currentKeyText) {
                      // We're changing from one key to another (not to/from no key)
                      setPendingKeyChange({
                        fromKey: selectedKey,
                        toKey: key,
                        selection: key
                      });
                      setIsKeySelectorModalOpen(false);
                      setIsKeyChangeMethodModalOpen(true);
                    } else {
                      // Direct key change (to/from no key or first time setting)
                      setSelectedKey(key);
                      if (key) {
                        setShowDegrees(true); // Automatically enable degrees when key is selected
                      } else {
                        setShowDegrees(false); // Disable degrees when no key is selected
                      }
                      setIsKeySelectorModalOpen(false);
                    }
                  }}
                  currentKeyDisplay={selectedKey}
                />

                {/* RN-ALIGNED: Error Display using theme object */}
                {fileError && (
                  <div style={styles.errorContainer} className="app-error-container">
                    <div
                      style={{
                        ...styles.errorBox,
                        backgroundColor: theme.colors.background,
                        borderColor: theme.colors.error,
                      }}
                    >
                      <span
                        style={{
                          ...styles.errorText,
                          color: theme.colors.error,
                        }}
                      >
                        {fileError}
                      </span>
                      <button
                        onClick={() => setFileError(null)}
                        style={{
                          ...styles.dismissButton,
                          color: theme.colors.primary,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '0.7';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                )}
                {/* Import Error Display (Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 7.4) */}
                {importError && (
                  <div style={styles.errorContainer} className="app-error-container">
                    <div
                      style={{
                        ...styles.errorBox,
                        backgroundColor: theme.colors.background,
                        borderColor: theme.colors.error,
                        flexDirection: 'column' as const,
                        alignItems: 'stretch',
                      }}
                    >
                      <span
                        style={{
                          ...styles.errorText,
                          color: theme.colors.error,
                          marginBottom: theme.spacing.md,
                        }}
                      >
                        {importError}
                      </span>
                      <div style={{ display: 'flex', gap: theme.spacing.sm, justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => {
                            setImportError(null);
                          }}
                          style={{
                            ...styles.dismissButton,
                            color: theme.colors.textSecondary,
                            padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
                            borderRadius: theme.radii.md,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '0.7';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '1';
                          }}
                        >
                          Go Home
                        </button>
                        <button
                          onClick={() => {
                            // Reload the page to try again (Requirement: 3.4)
                            window.location.reload();
                          }}
                          style={{
                            ...styles.dismissButton,
                            color: theme.colors.primary,
                            padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
                            borderRadius: theme.radii.md,
                            fontWeight: theme.typography.weights.bold,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '0.7';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '1';
                          }}
                        >
                          Try Again
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                <ConfirmationModal
                  isOpen={confirmationModalInfo.isOpen}
                  title={confirmationModalInfo.title}
                  message={confirmationModalInfo.message}
                  onConfirm={() => {
                    if (confirmationModalInfo.onConfirm) {
                      confirmationModalInfo.onConfirm();
                    }
                    setConfirmationModalInfo(prev => ({ ...prev, isOpen: false, onConfirm: () => { }, onAltConfirm: undefined, altConfirmText: undefined }));
                  }}
                  onCancel={() => {
                    if (confirmationModalInfo.onCancel) {
                      confirmationModalInfo.onCancel();
                    } else {
                      setConfirmationModalInfo(prev => ({ ...prev, isOpen: false, onConfirm: () => { }, onAltConfirm: undefined, altConfirmText: undefined }));
                    }
                  }}
                  confirmText={confirmationModalInfo.confirmText}
                  cancelText={confirmationModalInfo.cancelText}
                  showCancelButton={confirmationModalInfo.showCancelButton}
                  onAltConfirm={confirmationModalInfo.onAltConfirm ? () => {
                    if (confirmationModalInfo.onAltConfirm) {
                      confirmationModalInfo.onAltConfirm();
                    }
                    setConfirmationModalInfo(prev => ({ ...prev, isOpen: false, onConfirm: () => { }, onAltConfirm: undefined, altConfirmText: undefined }));
                  } : undefined}
                  altConfirmText={confirmationModalInfo.altConfirmText}
                />
                <PasteChartModal
                  isOpen={isPasteChartOpen}
                  onClose={() => setIsPasteChartOpen(false)}
                  onImport={handleImportPastedChart}
                />
                <ConfirmationModal
                  isOpen={isPasteReminderOpen}
                  title="Chart Imported"
                  message={CONSTANTS.PASTE_IMPORT_REVIEW_REMINDER}
                  confirmText="Got it"
                  showCancelButton={false}
                  onConfirm={() => setIsPasteReminderOpen(false)}
                  onCancel={() => setIsPasteReminderOpen(false)}
                />
                <div style={styles.mainContent} className="chord-sheet-area app-main-content">
                  <div style={styles.titleContainer} className="app-title-container">
                    {/* WEB-SPECIFIC: Global placeholder styling using theme */}
                    <style>
                      {`
                        textarea::placeholder {
                          color: ${theme.colors.textTertiary};
                          opacity: 1;
                        }
                      `}
                    </style>
                    <textarea
                      ref={titleTextareaRef}
                      rows={1}
                      placeholder="Title"
                      value={projectTitle}
                      onChange={(e) => setProjectTitle(e.target.value)}
                      style={styles.titleInput}
                      className="app-title-input"
                      onInput={(e) => {
                        const textarea = e.currentTarget;
                        textarea.style.height = 'auto';
                        textarea.style.height = `${textarea.scrollHeight}px`;
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                      }}
                    />
                  </div>
                  {/* Song key / time signature / tempo */}
                  <div style={styles.songMetaContainer}>
                    <div style={styles.songMetaInner}>
                    <SongMetaChips
                      songKey={selectedKey}
                      songMeta={songMeta}
                      showUnset={!!(activeSectionId || activeRowId || activeSlotId)}
                      onPressKey={() => setIsKeySelectorModalOpen(true)}
                      onPressTime={() => setSongMetaEditor('time')}
                      onPressTempo={() => setSongMetaEditor('tempo')}
                    />
                    </div>
                  </div>
                  <TimeSignatureEditorModal
                    isOpen={songMetaEditor === 'time'}
                    initialValue={songMeta.timeSignature}
                    onClose={() => setSongMetaEditor(null)}
                    onApply={applySongTimeSignature}
                    onClear={() => applySongTimeSignature(null)}
                  />
                  <TempoEditorModal
                    isOpen={songMetaEditor === 'tempo'}
                    initialValue={songMeta.tempo}
                    onClose={() => setSongMetaEditor(null)}
                    onApply={applyTempo}
                  />
                  <ChartReorderModal
                    isOpen={isReorderOpen}
                    sections={sections}
                    onClose={() => setIsReorderOpen(false)}
                    onApply={handleApplyChartDraft}
                  />
                  <div style={styles.sectionsContainer}>
                    {sections.map(sectionData => (
                      <Section
                        key={sectionData.id}
                        sectionData={sectionData}
                        onUpdateSectionTitle={handleUpdateSectionTitle}
                        onAddRowInSection={handleAddRowInSection}
                        onDuplicateRowInSection={handleDuplicateRowInSection}
                        onAddSlotInSection={handleAddSlot}
                        onAddSlotBeforeInSection={handleAddSlotBefore}
                        onDeleteSlotInSection={handleDeleteSlot}
                        onUpdateChordInSection={handleUpdateChord}
                        onUpdateLyricsInSection={handleUpdateLyrics}
                        onDeleteRowInSection={handleDeleteRowInSection}
                        onAddSectionAfterRowInSection={handleAddSectionAfterRow}
                        onSetRowTimeSignatureInSection={handleSetRowTimeSignature}
                        hasGlobalTimeSignature={songMeta.timeSignature !== null}
                        rowMeters={rowMeters}
                        showBeats={showBeats && !beatsNeedTimeSignature}
                        onApplyBarInSection={handleApplyBar}
                        onCantAddInsideInSection={handleCantAddInside}
                        onRequestSetTimeSignature={beatsNeedTimeSignature ? () => setSongMetaEditor('time') : undefined}
                        slotToOpenChordEditor={slotToOpenChordEditor}
                        onChordEditorOpened={() => setSlotToOpenChordEditor(null)}
                        onSetSectionRepeat={handleSetSectionRepeat}
                        onSetSectionNote={handleSetSectionNote}
                        onAddSectionAfterSection={handleAddSectionAfterSection}
                        onDeleteSection={handleDeleteSection}
                        isDeleteDisabled={sections.length === CONSTANTS.MIN_SECTIONS}
                        onDuplicateSection={handleDuplicateSection}
                        showNotes={showNotes}
                        showLyrics={showLyrics}
                        showDegrees={showDegrees && !!selectedKey}
                        selectedKey={selectedKey}
                        activeSectionId={activeSectionId}
                        onSectionPress={handleSectionPress}
                        onActivateSection={activateSection}
                        onClearActiveSection={clearActiveSection}
                        activeRowId={activeRowId}
                        onRowPress={handleRowPress}
                        onClearActiveRow={clearActiveRow}
                        activeSlotId={activeSlotId}
                        onSlotPress={handleSlotPress}
                        onClearActiveSlot={clearActiveSlot}
                        getControlVisibility={getControlVisibility}
                      />
                    ))}
                  </div>
                </div>
                <Footer />

                {/* Key Change Method Modal */}
                <KeyChangeMethodModal
                  isOpen={isKeyChangeMethodModalOpen}
                  onClose={() => {
                    setIsKeyChangeMethodModalOpen(false);
                    setPendingKeyChange(null);
                  }}
                  onSelectMethod={(method) => {
                    if (pendingKeyChange) {
                      if (method === 'relative') {
                        // Relative: Just change the key, keep the same chords
                        setSelectedKey(pendingKeyChange.selection);
                        setShowDegrees(true); // Automatically enable degrees when key is selected
                      } else if (method === 'transpose') {
                        // Transpose: Change the key and transpose all chords
                        const semitones = getKeyTranspositionInterval(
                          formatKeySignature(pendingKeyChange.fromKey),
                          formatKeySignature(pendingKeyChange.toKey)
                        );

                        // Update all chords in all sections
                        const updatedSections = mapSlots(sections, slot => {
                          // Skip custom chords and empty slots
                          if (slot.customText || !slot.chord?.root) {
                            return slot;
                          }

                          // Transpose directly with JSON API, spelling
                          // notes for the destination key (E♭ chart gets
                          // A♭, not G♯)
                          const transposedChord = transposeChord(slot.chord, semitones, pendingKeyChange.toKey) as ChordObjectModelV3;

                          return {
                            ...slot,
                            chord: transposedChord
                          };
                        });

                        setSections(updatedSections);
                        setSelectedKey(pendingKeyChange.selection);
                        setShowDegrees(true); // Automatically enable degrees when key is selected
                      }
                    }
                    setIsKeyChangeMethodModalOpen(false);
                    setPendingKeyChange(null);
                  }}
                  fromKey={pendingKeyChange ? formatKeySignature(pendingKeyChange.fromKey) : ''}
                  toKey={pendingKeyChange ? formatKeySignature(pendingKeyChange.toKey) : ''}
                />

                {/* Loading Modal */}
                <LoadingModal visible={isLoading} message={loadingMessage} />
              </div>
            </>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

// Main App component wrapped with ThemeProvider (following RN pattern)
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  );
};

export default App;
